import { styled } from '@linaria/react';
import { ActionIcon, Group, Menu, Stack } from '@mantine/core';
import { useCallback, useContext, useMemo, useState } from 'react';
import { icons } from '../../../assets/icons/icons';
import { UserContext } from '../../../contexts/user';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { setOrganization, setPortfolio } from '../../../store/global-store/global-store.actions';
import { setSearchTerm, setSelectedInitiative } from '../../../store/strategy-store/strategy-store.actions';
import { inkLight } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import type { ChatHistory } from '../../flora/flora.type';
import { useFloraUserPreferences } from '../../../api/agents-client/agent-client.hooks';
import { useDismissibleBanner } from '../../../hooks/useDismissibleBanner';
import { AgentAppSwitcher } from './agent-app-switcher';
import { AgentScopeMenu } from './agent-scope-menu';
import { arePreferencesEmpty } from './agent-landing/agent-landing.helpers';
import { FloraHistoryList } from '../../flora/components/flora-history-list/flora-history-list';
import { FloraNotificationStack } from './shared/flora-notification-stack';
import type { FloraNotificationItem } from './shared/flora-notification-stack';

interface SidebarActionButtonConfig {
  icon: string;
  collapsedIcon: string;
  label: string;
  onClick: () => void;
  shortcut?: string;
}

interface AgentHistoryPanelProps {
  chatHistories: ChatHistory[];
  activeChatId: string | undefined;
  isOpen: boolean;
  isDarkMode?: boolean;
  onToggle: () => void;
  onHistoryClick: (id: string) => void;
  onNewChat: () => void;
  onDelete: (id: string) => void;
  onEdit: (id: string, newDescription: string) => void;
  onSearch: () => void;
  onCommands: () => void;
  onDocuments: () => void;
  onAutomations: () => void;
  onPages: () => void;
  /** Opens the Flora settings area. */
  onSettings: () => void;
  /** Opens the settings Profile section (from the "Personalize Flora" card). */
  onPreferences: () => void;
  onToggleDarkMode: () => void;
  /** Whether to show the Documents nav item (deep research or Google connected). */
  showDocuments?: boolean;
}

export function AgentHistoryPanel({
  chatHistories,
  activeChatId,
  isOpen,
  isDarkMode,
  onToggle,
  onHistoryClick,
  onNewChat,
  onDelete,
  onEdit,
  onSearch,
  onCommands,
  onDocuments,
  onAutomations,
  onPages,
  onSettings,
  onPreferences,
  onToggleDarkMode,
  showDocuments,
}: AgentHistoryPanelProps) {
  const { user } = useContext(UserContext);
  const organization = useGlobalStore((state) => state.organization);
  const organizations = useGlobalStore((state) => state.organizations);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const portfolios = useGlobalStore((state) => state.portfolios);
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Sidebar "complete your preferences" card — independent test placement from
  // the landing banner, so it carries its own (per-org) dismissal key.
  const { preferences, isLoading: isLoadingPreferences } = useFloraUserPreferences();
  const { isDismissed: isPreferencesCardDismissed, dismiss: dismissPreferencesCard } = useDismissibleBanner(
    `complete-preferences-sidebar:${organization?.id ?? 'none'}`,
  );
  const showPreferencesCard = !isLoadingPreferences && arePreferencesEmpty(preferences) && !isPreferencesCardDismissed;

  const filteredPortfolios = useMemo(
    () => (organization ? portfolios.filter((p) => p.organization.id === organization.id) : portfolios),
    [organization, portfolios],
  );

  const handleOrgChange = useCallback(
    (orgId: string) => {
      setOrganization(organizations.find((o) => o.id === orgId) || null);
      setPortfolio(portfolios.find((p) => p.organization.id === orgId) || null);
      setSelectedInitiative(null);
      setSearchTerm('');
    },
    [organizations, portfolios],
  );

  const handlePortfolioChange = useCallback(
    (portfolioId: string) => {
      setPortfolio(portfolios.find((p) => p.id === portfolioId) || null);
      setSelectedInitiative(null);
      setSearchTerm('');
    },
    [portfolios],
  );

  const handlePreferencesClick = useCallback(() => {
    setUserMenuOpen(false);
    onPreferences();
  }, [onPreferences]);

  const handleSettingsClick = useCallback(() => {
    setUserMenuOpen(false);
    onSettings();
  }, [onSettings]);

  // Sidebar notification stack. Add further entries here; the stack shows pager
  // arrows automatically once more than one is active.
  const sidebarNotifications: FloraNotificationItem[] = [];
  if (showPreferencesCard) {
    sidebarNotifications.push({
      id: 'complete-preferences',
      icon: 'tune',
      title: 'Personalize Flora',
      description: 'Add your name, role, and priorities so Flora can tailor its answers.',
      actionLabel: 'Set up preferences',
      onAction: handlePreferencesClick,
      onDismiss: dismissPreferencesCard,
    });
  }

  const isMac = /Mac|iPhone|iPad/.test(navigator.platform);

  // "New chat" and "Search" are pinned at the top in this fixed order; every
  // other nav item is sorted alphabetically by label beneath them.
  const topActionButtons: SidebarActionButtonConfig[] = [
    {
      icon: 'add',
      collapsedIcon: 'edit_square',
      label: 'New chat',
      onClick: onNewChat,
      shortcut: isMac ? '⌘⇧O' : 'Ctrl⇧O',
    },
    {
      icon: 'search',
      collapsedIcon: 'search',
      label: 'Search',
      onClick: onSearch,
      shortcut: isMac ? '⌘K' : 'CtrlK',
    },
  ];

  const navActionButtons: SidebarActionButtonConfig[] = [
    ...(showDocuments
      ? [
          {
            icon: 'description',
            collapsedIcon: 'description',
            label: 'Documents',
            onClick: onDocuments,
          },
        ]
      : []),
    {
      icon: 'terminal',
      collapsedIcon: 'terminal',
      label: 'Commands',
      onClick: onCommands,
    },
    {
      icon: 'bolt',
      collapsedIcon: 'bolt',
      label: 'Automations',
      onClick: onAutomations,
    },
    {
      icon: 'article',
      collapsedIcon: 'article',
      label: 'Pages',
      onClick: onPages,
    },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const sidebarActionButtons = [...topActionButtons, ...navActionButtons];

  if (!isOpen) {
    return (
      <Stack
        align="center"
        gap="sm"
        style={{
          width: 52,
          height: '100%',
          background: 'var(--sky-lightest)',
          borderRight: '1px solid var(--sky-light)',
          flexShrink: 0,
          paddingTop: 12,
        }}
      >
        <Tooltip label="Expand sidebar" variant="black" position="right">
          <ActionIcon
            variant="subtle"
            color="var(--ink-dark)"
            onClick={onToggle}
            aria-label="Expand sidebar"
            radius="md"
            style={{ cursor: 'pointer', marginBottom: 16 }}
          >
            <img src={icons.iconFlora} alt="" aria-hidden="true" width={28} height={28} />
          </ActionIcon>
        </Tooltip>
        {sidebarActionButtons.map((button) => (
          <Tooltip key={button.icon} label={button.label} variant="black" position="right">
            <ActionIcon
              variant="subtle"
              color="var(--ink-dark)"
              onClick={button.onClick}
              aria-label={button.label}
              radius="md"
              style={{ cursor: 'pointer' }}
            >
              <Icon name={button.icon} color="var(--ink-dark)" size={20} style={{ cursor: 'pointer' }} />
            </ActionIcon>
          </Tooltip>
        ))}
        <CollapsedUserSection>
          <Divider color="var(--sky-light)" />
          <Menu
            shadow="md"
            radius="md"
            position="top"
            offset={8}
            opened={userMenuOpen}
            onChange={setUserMenuOpen}
            withinPortal={false}
          >
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="var(--ink-dark)"
                aria-label="User"
                radius="md"
                style={{ cursor: 'pointer' }}
                onClick={() => setUserMenuOpen((prev) => !prev)}
              >
                <Icon name="account_circle" color="var(--ink-dark)" size={24} style={{ cursor: 'pointer' }} />
              </ActionIcon>
            </Menu.Target>
            <AgentScopeMenu
              organization={organization}
              organizations={organizations}
              portfolio={portfolio}
              filteredPortfolios={filteredPortfolios}
              isDarkMode={isDarkMode}
              onOrgChange={handleOrgChange}
              onPortfolioChange={handlePortfolioChange}
              onToggleDarkMode={onToggleDarkMode}
              onSettings={handleSettingsClick}
            />
          </Menu>
        </CollapsedUserSection>
      </Stack>
    );
  }

  return (
    <Panel>
      <TopBar>
        <AgentAppSwitcher />
        <ActionIcon
          variant="subtle"
          color="var(--ink-dark)"
          onClick={onToggle}
          aria-label="Collapse sidebar"
          radius="md"
          style={{ cursor: 'pointer' }}
        >
          <Icon name="chevron_left" color={inkLight} size={24} style={{ cursor: 'pointer' }} />
        </ActionIcon>
      </TopBar>

      {sidebarActionButtons.map((button) => (
        <SidebarActionButton key={button.icon} gap="xs" onClick={button.onClick}>
          <Icon name={button.icon} color="var(--ink-dark)" size={20} style={{ cursor: 'pointer' }} />
          <Text size="regular" weight="bold" color="var(--ink-dark)">
            {button.label}
          </Text>
          <Text
            size="small"
            color="var(--ink-lighter)"
            className="shortcut-hint"
            style={{
              marginLeft: 'auto',
              paddingRight: 8,
              opacity: 0,
              transition: 'opacity 0.12s',
              flexShrink: 0,
              userSelect: 'none',
            }}
          >
            {button.shortcut}
          </Text>
        </SidebarActionButton>
      ))}

      <ChatSectionHeader onClick={() => setHistoryCollapsed((prev) => !prev)}>
        <Text size="regular" weight="bold" color="var(--ink-lighter)">
          Chats
        </Text>
        <Icon
          name={historyCollapsed ? 'chevron_right' : 'expand_more'}
          color="var(--ink-lighter)"
          size={18}
          style={{ cursor: 'pointer' }}
        />
      </ChatSectionHeader>

      {!historyCollapsed && (
        <HistoryList>
          <FloraHistoryList
            chatHistories={chatHistories}
            activeChatId={activeChatId}
            onSelect={onHistoryClick}
            onDelete={onDelete}
            onEdit={onEdit}
          />
        </HistoryList>
      )}

      <BottomCluster>
        {sidebarNotifications.length > 0 && (
          <SidebarBannerSlot>
            <FloraNotificationStack notifications={sidebarNotifications} />
          </SidebarBannerSlot>
        )}

        <UserSection>
          <Divider color="var(--sky-light)" style={{ width: 'calc(100% - 32px)', margin: '0 16px' }} />
          <Menu
            shadow="md"
            radius="md"
            position="top-start"
            offset={4}
            opened={userMenuOpen}
            onChange={setUserMenuOpen}
            withinPortal={false}
          >
            <Menu.Target>
              <UserInfo onClick={() => setUserMenuOpen((prev) => !prev)}>
                <ActionIcon
                  variant="subtle"
                  color="var(--ink-dark)"
                  aria-label="User"
                  radius="md"
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                >
                  <Icon name="account_circle" color="var(--ink-dark)" size={24} style={{ cursor: 'pointer' }} />
                </ActionIcon>
                <UserDetails>
                  <Text size="small" weight="bold" color="var(--ink-dark)">
                    {user?.name}
                  </Text>
                  {(organization || portfolio) && (
                    <OrgPortfolioLine size="tiny" color="var(--ink-lighter)">
                      {organization?.name}
                      {organization && portfolio && ' | '}
                      {portfolio?.name}
                    </OrgPortfolioLine>
                  )}
                </UserDetails>
                <Icon
                  name={userMenuOpen ? 'expand_more' : 'chevron_right'}
                  color="var(--ink-lighter)"
                  size={16}
                  style={{ marginLeft: 'auto', flexShrink: 0 }}
                />
              </UserInfo>
            </Menu.Target>
            <AgentScopeMenu
              organization={organization}
              organizations={organizations}
              portfolio={portfolio}
              filteredPortfolios={filteredPortfolios}
              isDarkMode={isDarkMode}
              onOrgChange={handleOrgChange}
              onPortfolioChange={handlePortfolioChange}
              onToggleDarkMode={onToggleDarkMode}
              onSettings={handleSettingsClick}
            />
          </Menu>
        </UserSection>
      </BottomCluster>
    </Panel>
  );
}

const Panel = styled.div`
  width: 260px;
  min-width: 260px;
  height: 100%;
  background: var(--sky-lightest);
  border-right: 1px solid var(--sky-light);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 10px 20px 14px;
  flex-shrink: 0;
`;

const SidebarActionButton = styled(Group)`
  margin: 0 12px 2px;
  padding: 6px 4px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--sky-light);

    .shortcut-hint {
      opacity: 1 !important;
    }
  }
`;

const ChatSectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 12px 4px;
  padding: 8px 4px;
  border-radius: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover {
    background: var(--sky-light);
  }
`;

const HistoryList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 16px;
`;

const BottomCluster = styled.div`
  margin-top: auto;
  flex-shrink: 0;
`;

const SidebarBannerSlot = styled.div`
  padding: 8px 12px 4px;
  flex-shrink: 0;
`;

const UserSection = styled.div`
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.12s;

  &:hover {
    background: var(--sky-lighter);
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const OrgPortfolioLine = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CollapsedUserSection = styled.div`
  margin-top: auto;
  padding-bottom: 10px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;
